/**
 * Chave de idempotência client-side (AMVP-05/08 · IMVP-13). Gerada uma vez por
 * "tentativa lógica" (fingerprint estável do payload) e reaproveitada em retries
 * automáticos/manuais do MESMO payload; muda assim que o payload muda de verdade.
 * Guardado só em memória (módulo), nunca em storage — não é segredo, mas também
 * não precisa sobreviver a reload (um reload é uma nova tentativa legítima).
 *
 * **Quando derivar do payload NÃO serve:** se o próprio payload muda a cada
 * tentativa (o prazo do wizard é "agora + N", recalculado no disparo), a chave
 * derivada dele muda junto e cada retry vira um recurso NOVO no servidor —
 * exatamente o que a idempotência existia para impedir. Nesses casos a chave é
 * ESTADO de quem dispara, não função do conteúdo: ver `newIdempotencyKey`.
 */

const keysByFingerprint = new Map<string, string>();

function randomKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback só para ambientes de teste sem `crypto.randomUUID` (Node < 19 puro).
  return `idem_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

/** `JSON.stringify` com chaves ordenadas recursivamente — determinístico
 * independente da ordem de construção do objeto em qualquer nível. */
function sortDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortDeep);
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
      a.localeCompare(b),
    );
    return Object.fromEntries(entries.map(([key, val]) => [key, sortDeep(val)]));
  }
  return value;
}

export function stableFingerprint(value: unknown): string {
  return JSON.stringify(sortDeep(value));
}

export function idempotencyKeyFor(scope: string, fingerprint: string): string {
  const cacheKey = `${scope}:${fingerprint}`;
  const existing = keysByFingerprint.get(cacheKey);
  if (existing) return existing;
  const created = randomKey();
  keysByFingerprint.set(cacheKey, created);
  return created;
}

/**
 * Chave nova, sem cache e sem fingerprint — para uma **série de disparo**: quem
 * dispara guarda a chave e a reutiliza em TODO retry da mesma série, até
 * abandonar a série (no wizard, sair da Revisão para editar). É o que faz o
 * backend idempotente devolver a resposta ORIGINAL quando a primeira resposta se
 * perde na rede, em vez de criar um segundo rascunho.
 *
 * Chave estável **e pedido estável**: o backend também confere um `requestHash`
 * do corpo e responde 409 na mesma chave com payload diferente. Quem congela a
 * chave precisa congelar junto tudo que o payload carrega de relógio.
 */
export function newIdempotencyKey(): string {
  return randomKey();
}

/** Só para teste/depuração — nunca chamado pelo app em uso normal. */
export function _resetIdempotencyCacheForTests(): void {
  keysByFingerprint.clear();
}
